const GAP = 2;

/**
 * Looks for the best possible fit of rows and columns for a given size and a given number of squares that will be placed inside
 * @param containerWidth -
 * @param containerHeight -
 * @param childCount -
 */
export function calculateGridLayout(
  containerWidth: number,
  containerHeight: number,
  childCount: number,
) {
  if (containerWidth === 0 || containerHeight === 0 || childCount === 0) {
    return {
      columnCount: 0,
      rowCount: 0,
      lastRowColumnCount: 0,
      gap: '0px',
    };
  }

  const childSize = calculateChildSize(
    containerWidth,
    containerHeight,
    childCount,
  );

  const childCountPerRow = Math.floor(containerWidth / childSize);

  const rowCount = Math.ceil(childCount / childCountPerRow);
  const columnCount = Math.ceil(childCount / rowCount);

  const lastRowColumnCount =
    columnCount - (columnCount * rowCount - childCount);

  return {
    columnCount,
    rowCount,
    lastRowColumnCount,
    gap: `${GAP}px`,
  };
}

/**
 * Calculates the child size for the best possible distribution inside a grid
 *
 * Source: https://math.stackexchange.com/questions/466198/algorithm-to-get-the-maximum-size-of-n-squares-that-fit-into-a-rectangle-with-a
 *
 * @param containerWidth - the container width
 * @param containerHeight - the container height
 * @param childCount - the item count
 */
function calculateChildSize(
  containerWidth: number,
  containerHeight: number,
  childCount: number,
): number {
  const horizontalFitSquareCount = calculatePotentialFittingSquares(
    containerWidth,
    containerHeight,
    childCount,
  );

  const verticalFitSquareCount = calculatePotentialFittingSquares(
    containerWidth,
    containerHeight,
    childCount,
  );

  const fittingWidth = calculateFittingSideLength(
    containerWidth,
    containerHeight,
    horizontalFitSquareCount,
    childCount,
  );

  const fittingHeight = calculateFittingSideLength(
    containerHeight,
    containerWidth,
    verticalFitSquareCount,
    childCount,
  );

  return Math.max(fittingWidth, fittingHeight);
}

/**
 * Calculate the potential number of child squares that can fit along a dimension of the container.
 *
 * @param containerSize1 - The first dimension of the container (either width or height).
 * @param containerSize2 - The second dimension of the container (either height or width).
 * @param childCount - The number of child squares to fit into the container.
 * @returns The potential number of child squares that can fit along the given dimension.
 */
function calculatePotentialFittingSquares(
  containerSize1: number,
  containerSize2: number,
  childCount: number,
): number {
  return Math.ceil(Math.sqrt((childCount * containerSize1) / containerSize2));
}

/**
 * Calculate the side length that allows child squares to fit into the container.
 *
 * @param containerSize1 - The first dimension of the container (either width or height).
 * @param containerSize2 - The second dimension of the container (either height or width).
 * @param potentialFitSquares - The potential number of child squares that can fit along the given dimension.
 * @param childCount - The number of child squares to fit into the container.
 * @returns The side length that allows child squares to fit into the container.
 */
function calculateFittingSideLength(
  containerSize1: number,
  containerSize2: number,
  potentialFitSquares: number,
  childCount: number,
): number {
  if (
    Math.floor((potentialFitSquares * containerSize2) / containerSize1) *
      potentialFitSquares <
    childCount
  ) {
    return (
      containerSize2 /
      Math.ceil((potentialFitSquares * containerSize2) / containerSize1)
    );
  } else {
    return containerSize1 / potentialFitSquares;
  }
}
